import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  FaRoute,
  FaNotesMedical,
  FaHeartbeat,
  FaDna,
  FaCalendarCheck,
  FaLock,
  FaArrowRight,
  FaUserMd,
  FaHospital,
  FaUsers,
  FaStethoscope,
  FaCheckCircle,
  FaStar,
  FaQuoteLeft,
  FaMobileAlt,
  FaShieldAlt,
  FaCloud,
  FaChartLine,
  FaLeaf,
} from "react-icons/fa";

const LandingPage = () => {
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const [isScrolled, setIsScrolled] = useState(false);
  const [billing, setBilling] = useState("quarterly");
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleGetStarted = () => {
    navigate("/login");
  };

  const features = [
    {
      icon: <FaRoute />,
      title: "Wizard-based Flow",
      description:
        "Intuitive step-by-step process guides you through patient management effortlessly",
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: <FaNotesMedical />,
      title: "Structured Records",
      description:
        "Organized patient documentation system that keeps everything in perfect order",
      color: "from-green-500 to-emerald-500",
    },
    {
      icon: <FaHeartbeat />,
      title: "Parikshan Modules",
      description:
        "Comprehensive examination tools designed specifically for Ayurvedic practices",
      color: "from-red-500 to-pink-500",
    },
    {
      icon: <FaDna />,
      title: "Kulawrutta Support",
      description:
        "Advanced family history tracking to understand patient lineage and patterns",
      color: "from-purple-500 to-indigo-500",
    },
    {
      icon: <FaCalendarCheck />,
      title: "Follow-up Scheduling",
      description:
        "Never miss an appointment with intelligent scheduling and reminder system",
      color: "from-orange-500 to-amber-500",
    },
    {
      icon: <FaLock />,
      title: "Secure, Online Access",
      description:
        "Bank-level security ensures your patient data is always protected and accessible",
      color: "from-teal-500 to-cyan-500",
    },
  ];

  const steps = [
    {
      number: "1",
      title: "Sign up for free",
      description:
        "Create your account in minutes with no credit card required",
      icon: <FaUserMd />,
    },
    {
      number: "2",
      title: "Add patient details",
      description:
        "Start documenting patient information with our intuitive forms",
      icon: <FaNotesMedical />,
    },
    {
      number: "3",
      title: "Run parikshans & record",
      description:
        "Conduct examinations systematically using our structured modules",
      icon: <FaHeartbeat />,
    },
    {
      number: "4",
      title: "Prescribe & Follow-up",
      description: "Create prescriptions and schedule visits seamlessly",
      icon: <FaCalendarCheck />,
    },
    {
      number: "5",
      title: "Access anytime, anywhere",
      description:
        "Cloud-based platform ensures you can access data from any device",
      icon: <FaCloud />,
    },
  ];

  const targetAudience = [
    {
      icon: <FaUserMd />,
      title: "Solo Doctors",
      description:
        "Perfect for individual practitioners managing their own practice",
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      icon: <FaStethoscope />,
      title: "Panchakarma Clinics",
      description:
        "Specialized treatment centers requiring detailed patient tracking",
      gradient: "from-green-500 to-emerald-500",
    },
    {
      icon: <FaHospital />,
      title: "Ayurveda Hospitals",
      description:
        "Large-scale healthcare facilities needing comprehensive management",
      gradient: "from-purple-500 to-indigo-500",
    },
    {
      icon: <FaUsers />,
      title: "Multi-Doctor Clinics",
      description:
        "Collaborative practice management for teams of practitioners",
      gradient: "from-orange-500 to-amber-500",
    },
  ];

  const pricingPlans = [
    {
      name: "Free Trial",
      description: "Try all features risk-free",
      price: "Free",
      period: "30 Days",
      features: [
        "Full feature access",
        "Unlimited patients",
        "No credit card required",
        "Cancel anytime",
        "Email support",
      ],
      isFree: true,
      popular: false,
    },
    {
      name: "Solo Plan",
      description: "For individual Ayurvedic practitioners",
      quarterly: 1500,
      yearly: 5000,
      features: [
        "Single doctor license",
        "Unlimited patients",
        "All core modules",
        "Data backup",
        "Priority support",
      ],
      isFree: false,
      popular: false,
    },
    {
      name: "Clinic Plan",
      description: "For multi-doctor clinics & hospitals",
      quarterly: 3000,
      yearly: 10000,
      features: [
        "Multiple doctor accounts",
        "Team collaboration",
        "Advanced reporting",
        "Role-based access",
        "Dedicated support",
      ],
      isFree: false,
      popular: true,
    },
  ];

  const stats = [
    { number: "500+", label: "Active Practitioners", icon: <FaUserMd /> },
    { number: "50K+", label: "Patients Managed", icon: <FaNotesMedical /> },
    { number: "99.9%", label: "Uptime", icon: <FaCloud /> },
    { number: "24/7", label: "Support", icon: <FaShieldAlt /> },
  ];

  const testimonials = [
    {
      name: "Dr. Pradnya Kulkarni",
      role: "Ayurvedic Practitioner",
      content:
        "Ayurmax had made ayurvedic doctor's clinical examination and record of treatment very easy. Need of the our for Ayurvedic practitioners.",
      rating: 5,
      avatar: "👩‍⚕️",
    },
    {
      name: "Dr. Amit Choudhary",
      role: "Ayurvedic Practitioner",
      content:
        "I have been using Ayurmax for several years. It gives me a quick access to patient history and other important details at a glance.",
      rating: 5,
      avatar: "👨‍⚕️",
    },
    {
      name: "Dr. Seema Pathade",
      role: "Ayurvedic Practitioner",
      content:
        "Ayurmax has helped me manage patient data systematically. Found the Ayurmax platform very easy to use.",
      rating: 5,
      avatar: "👩‍⚕️",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-ayurmax-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-ayurmax-primary/10 rounded-full blur-3xl"></div>
        <motion.div
          animate={{
            y: [0, -20, 0],
            rotate: [0, 5, 0],
            opacity: [0.1, 0.3, 0.1],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/3 left-10 text-green-300/30"
        >
          <FaLeaf size={120} />
        </motion.div>
        <motion.div
          animate={{
            y: [0, 30, 0],
            rotate: [0, -10, 0],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
          className="absolute bottom-1/4 right-10 text-green-400/20"
        >
          <FaLeaf size={180} />
        </motion.div>
      </div>

      {/* Header */}
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-md shadow-lg"
            : "bg-transparent"
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className={`text-3xl font-bold transition-colors ${
                isScrolled ? "text-ayurmax-primary" : "text-white"
              }`}
            >
              <Link to="/" className="flex items-center">
                <motion.img
                  whileHover={{ scale: 1.05 }}
                  src="/Ayumaxfinal.png"
                  alt="Ayumax"
                  className="h-16 md:h-20"
                />
              </Link>
            </motion.div>
            <nav className="hidden md:flex items-center space-x-8">
              <a
                href="#features"
                className={`hover:text-ayurmax-primary transition-colors ${
                  isScrolled ? "text-gray-700" : "text-white"
                }`}
              >
                Features
              </a>
              <a
                href="#how"
                className={`hover:text-ayurmax-primary transition-colors ${
                  isScrolled ? "text-gray-700" : "text-white"
                }`}
              >
                How It Works
              </a>
              <a
                href="#pricing"
                className={`hover:text-ayurmax-primary transition-colors ${
                  isScrolled ? "text-gray-700" : "text-white"
                }`}
              >
                Pricing
              </a>
              <Link
                to="/contact-us"
                className={`hover:text-ayurmax-primary transition-colors ${
                  isScrolled ? "text-gray-700" : "text-white"
                }`}
              >
                Contact
              </Link>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleGetStarted}
                className={`px-6 py-2 rounded-full font-semibold transition-all ${
                  isScrolled
                    ? "bg-ayurmax-primary text-white hover:bg-ayurmax-dark"
                    : "bg-white text-ayurmax-primary hover:bg-gray-100"
                } shadow-lg`}
              >
                Login
              </motion.button>
            </nav>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleGetStarted}
              className="md:hidden px-4 py-2 bg-ayurmax-primary text-white rounded-full font-semibold text-sm shadow-lg"
            >
              Login
            </motion.button>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="mb-20 relative min-h-screen flex items-center pt-20 overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-lime-50 via-ayurmax-primary to-ayurmax-dark"></div>

        {/* Animated Circles */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.2, 0.1],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1.2, 1, 1.2],
              opacity: [0.1, 0.2, 0.1],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"
          />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8 text-white"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-semibold"
              >
                🎉 Trusted by Ayurvedic Practitioners
              </motion.div>

              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                Ayurveda Meets <br />
                <span className=" bg-gradient-to-r from-yellow-300 to-green-300 bg-clip-text text-transparent">
                  Technology
                </span>
              </h1>

              <p className="text-xl  md:text-2xl text-white/90 leading-relaxed max-w-xl">
                Complete, secure and intuitive clinic software for modern
                Ayurvedic practitioners. Transform your practice with digital
                excellence.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleGetStarted}
                  className="bg-white text-ayurmax-primary px-8 py-4 rounded-full text-lg font-bold shadow-2xl hover:shadow-white/50 transition-all flex items-center justify-center gap-2 group"
                >
                  Login
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate("/contact-us")}
                  className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full text-lg font-semibold border-2 border-white/30 hover:bg-white/20 transition-all"
                >
                  Contact Us
                </motion.button>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center gap-8 pt-4">
                <div className="flex items-center gap-2">
                  <FaShieldAlt className="text-green-300" />
                  <span className="text-sm">Bank-level Security</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaCloud className="text-blue-300" />
                  <span className="text-sm">Cloud-based</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaMobileAlt className="text-purple-300" />
                  <span className="text-sm">Mobile Ready</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="relative">
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-ayurmax-primary to-green-500 rounded-3xl blur-2xl opacity-50"></div>

                {/* Main Image Container */}
                <motion.div
                  whileHover={{ scale: 1.02, rotate: 1 }}
                  className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border border-white/20"
                >
                  <div className="bg-gradient-to-br from-white to-gray-100 rounded-2xl  shadow-xl">
                    <img
                      src="/LandingPage.png"
                      alt="Ayurmax Application Screenshot"
                      className="rounded-xl w-full h-auto"
                    />
                  </div>

                  {/* Floating Cards */}
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className="absolute -top-4 -right-4 bg-white rounded-xl p-4 shadow-xl"
                  >
                    <div className="flex items-center gap-2">
                      <FaChartLine className="text-green-500" />
                      <span className="text-sm font-bold text-gray-800">
                        Analytics
                      </span>
                    </div>
                  </motion.div>

                  <motion.div
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 3, repeat: Infinity, delay: 1 }}
                    className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-xl"
                  >
                    <div className="flex items-center gap-2">
                      <FaCalendarCheck className="text-blue-500" />
                      <span className="text-sm font-bold text-gray-800">
                        Schedule
                      </span>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className=" py-10 bg-white relative -mt-20 z-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
                className="text-center p-6 bg-gradient-to-br from-ayurmax-primary/10 to-ayurmax-dark/10 rounded-2xl border border-ayurmax-primary/20"
              >
                <div className="text-4xl text-ayurmax-primary mb-2 flex justify-center">
                  {stat.icon}
                </div>
                <div className="text-3xl md:text-4xl font-bold text-ayurmax-primary mb-1">
                  {stat.number}
                </div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section
        id="features"
        className="py-24 bg-gradient-to-b from-white to-gray-50"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-ayurmax-primary mb-4">
              Powerful Features
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to manage your Ayurvedic practice efficiently
              and professionally
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                className="group relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border border-gray-100 overflow-hidden"
              >
                {/* Gradient Background on Hover */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-5 transition-opacity`}
                ></div>

                <div
                  className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${feature.color} text-white mb-4 text-3xl shadow-lg`}
                >
                  {feature.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how" className="py-24 bg-white relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-ayurmax-primary mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get started in five simple steps and transform your practice today
            </p>
          </motion.div>

          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-ayurmax-primary via-green-400 to-ayurmax-primary"></div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-5 gap-8 relative"
            >
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ scale: 1.05 }}
                  className="relative"
                >
                  <div className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border-2 border-transparent hover:border-ayurmax-primary/30 text-center relative z-10">
                    <div className="w-20 h-20 bg-gradient-to-br from-ayurmax-primary to-ayurmax-dark text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg">
                      {step.number}
                    </div>
                    <div className="text-3xl text-ayurmax-primary mb-4 flex justify-center">
                      {step.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* For Whom Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-ayurmax-primary mb-4">
              Perfect For Every Practice
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Designed to scale with practices of all sizes
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {targetAudience.map((audience, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                className="group relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all border border-gray-100 overflow-hidden"
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${audience.gradient} opacity-0 group-hover:opacity-10 transition-opacity`}
                ></div>
                <div
                  className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${audience.gradient} text-white mb-4 text-3xl shadow-lg`}
                >
                  {audience.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  {audience.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {audience.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-ayurmax-primary mb-4">
              Loved by Practitioners
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              See what our users have to say about Ayurmax
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-lg border border-gray-100"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} className="text-yellow-400" />
                  ))}
                </div>
                <FaQuoteLeft className="text-ayurmax-primary text-2xl mb-4" />
                <p className="text-gray-700 mb-6 leading-relaxed">
                  {testimonial.content}
                </p>
                <div className="flex items-center gap-3">
                  <div className="text-3xl">{testimonial.avatar}</div>
                  <div>
                    <div className="font-bold text-gray-800">
                      {testimonial.name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {testimonial.role}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section
        id="pricing"
        className="py-24 bg-gradient-to-b from-gray-50 to-white"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-ayurmax-primary mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Choose the plan that fits your practice. All plans include a
              60-day free trial.
            </p>
          </motion.div>

          {/* Billing Toggle */}
          <div className="flex justify-center mb-12">
            <div className="bg-gray-100 p-1 rounded-full flex">
              <button
                onClick={() => setBilling("quarterly")}
                className={`px-6 py-2 rounded-full text-sm font-semibold transition ${
                  billing === "quarterly"
                    ? "bg-white shadow text-ayurmax-primary"
                    : "text-gray-600"
                }`}
              >
                Quarterly
              </button>
              <button
                onClick={() => setBilling("yearly")}
                className={`px-6 py-2 rounded-full text-sm font-semibold transition ${
                  billing === "yearly"
                    ? "bg-white shadow text-ayurmax-primary"
                    : "text-gray-600"
                }`}
              >
                Yearly
              </button>
            </div>
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto"
          >
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -8 }}
                className={`relative bg-white p-8 rounded-3xl border transition-all shadow-lg ${
                  plan.popular
                    ? "border-ayurmax-primary shadow-2xl scale-105"
                    : "border-gray-200"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-ayurmax-primary text-white px-5 py-1 rounded-full text-xs font-bold">
                    Most Popular
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-gray-500 text-sm mb-6">
                    {plan.description}
                  </p>

                  {plan.isFree ? (
                    <>
                      <div className="text-5xl font-bold text-ayurmax-primary">
                        {plan.price}
                      </div>
                      <div className="text-gray-500 mt-2">{plan.period}</div>
                    </>
                  ) : (
                    <>
                      <div className="text-5xl font-bold text-ayurmax-primary">
                        {billing === "quarterly" ? plan.quarterly.toLocaleString('en-IN', { style: 'currency', currency: 'INR',minimumFractionDigits: 0, maximumFractionDigits: 0 }) : plan.yearly.toLocaleString('en-IN', { style: 'currency', currency: 'INR',minimumFractionDigits: 0, maximumFractionDigits: 0 }) }
                      </div>
                      <div className="text-gray-500 mt-2">
                        {billing === "quarterly" ? "per quarter" : "per year"}
                      </div>
                    </>
                  )}
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-3 text-gray-700"
                    >
                      <FaCheckCircle className="text-green-500 mt-1 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => navigate("/contact-us")}
                  className={`w-full py-3 rounded-xl font-semibold transition ${
                    plan.popular
                      ? "bg-ayurmax-primary text-white hover:bg-ayurmax-dark"
                      : "bg-gray-100 text-ayurmax-primary hover:bg-gray-200"
                  }`}
                >
                  {plan.isFree ? "Start Free Trial" : "Get Started"}
                </motion.button>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-b from-ayurmax-primary to-ayurmax-dark relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto space-y-8"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Bring Your Practice Into the <br />
              <span className=" bg-gradient-to-r from-yellow-300 to-green-300 bg-clip-text text-transparent">
                Digital Age
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-white/90 mb-8">
              Join hundreds of Ayurvedic practitioners who trust Ayurmax to
              manage their practice efficiently
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/contact-us")}
                className="bg-white text-ayurmax-primary px-10 py-5 rounded-full text-xl font-bold shadow-2xl hover:shadow-white/50 transition-all flex items-center justify-center gap-2 group"
              >
                Contact Us
                <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-ayurmax-dark text-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Link to="/" className="flex items-center">
                  <motion.img
                    whileHover={{ scale: 1.05 }}
                    src="/Ayumaxfinal.png"
                    alt="Ayumax"
                    className="h-8 md:h-10"
                  />
                </Link>
              </div>
              <p className="text-white/70">
                Modern clinic software for Ayurvedic practitioners.
              </p>
            </div>

            <div>
              <h3 className="font-bold mb-4">Product</h3>
              <ul className="space-y-2 text-white/70">
                <li>
                  <a
                    href="#features"
                    className="hover:text-white transition-colors"
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a
                    href="#pricing"
                    className="hover:text-white transition-colors"
                  >
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="#how" className="hover:text-white transition-colors">
                    How It Works
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-4">Support</h3>
              <ul className="space-y-2 text-white/70">
                <li>
                  <Link
                    to="/contact-us"
                    className="hover:text-white transition-colors"
                  >
                    Contact Us
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-4">Contact</h3>
              <ul className="space-y-2 text-white/70">
                <li>
                  <a
                    href="mailto:reachus@pitlabs.in"
                    className="hover:text-white transition-colors"
                  >
                    reachus@pitlabs.in
                  </a>
                </li>
                <li>WhatsApp Support Available</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 text-center text-white/50">
            <p>&copy; 2026 PIT Labs. All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
